import get from 'lodash/get';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

export const getWorkOrderStatusData = (status) => {
	switch (status) {
	case 'in_preparation':
		return {
			text: 'IN PREPARATION',
			color: '#666666'
		};
	case 'ready_for_work':
		return {
			text: 'READY FOR WORK',
			color: '#108ee9'
		};
	case 'in_progress':
		return {
			text: 'IN PROGRESS',
			color: '#2db7f5'
		};
	case 'done':
		return {
			text: 'DONE',
			color: '#87d068'
		};
	case 'closed':
		return {
			text: 'CLOSED',
			color: '#faad14'
		};
	default:
		return {
			text: 'IN PREPARATION',
			color: '#666666'
		};
	}
};

export const getCustomFields = (userSelf, objectType, appliesTo) => {
	if (!objectType) {
		return Object.values(get(userSelf, 'active_organization.custom_fields', {})).flat();
	}

	const customFields = get(userSelf, `active_organization.custom_fields.${objectType}`, []) || [];

	if (appliesTo) {
		return customFields.filter(field => !field.applies_to || field.applies_to.includes(appliesTo));
	}

	return customFields;
};


export const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

		return () => {
			clearTimeout(timer);
		};
	}, [value, delay]);

	return debouncedValue;
};

export const getActiveWorkDescription = (workEntry) => {
	switch (workEntry.parent_type) {
	case 'work_order_op':
		if (get(workEntry.work_meta, 'work_order_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'nr')} / ${get(workEntry.work_meta, 'name') || get(workEntry.work_meta, 'op_nr')}`;
		}

		if (get(workEntry.work_meta, 'tech_log_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'tlog')} / ${get(workEntry.work_meta, 'name') || get(workEntry.work_meta, 'op_nr')}`;
		}
		return '';
	case 'defect':
		if (get(workEntry.work_meta, 'work_order_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'nr')} / ${get(workEntry.work_meta, 'description')}`;
		}

		if (get(workEntry.work_meta, 'tech_log_id')) {
			return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
				${get(workEntry.work_meta, 'tlog')} / ${get(workEntry.work_meta, 'description')}`;
		}
		return '';
	case 'work_order':
		return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
			${get(workEntry.work_meta, 'nr')}`;
	case 'tech_log':
		return `${get(workEntry.work_meta, 'registration') ? `${get(workEntry.work_meta, 'registration')} / ` : ''}
			${get(workEntry.work_meta, 'tlog')}`;
	default:
		return get(workEntry, 'other_type_value') || 'Other work';
	}
};

export const getFormattedDuration = (dur, displaySeconds) => {
	const duration = moment.duration(dur, 'milliseconds');
	const hours = (Math.floor(duration.asHours()))
		.toLocaleString(undefined, { minimumIntegerDigits: 2 });
	const mins = (Math.floor(duration.asMinutes()) - (hours * 60))
		.toLocaleString(undefined, { minimumIntegerDigits: 2 });
	const sec = (Math.floor(duration.asSeconds()) - (hours * 60 * 60) - (mins * 60))
		.toLocaleString(undefined, { minimumIntegerDigits: 2 });

	const result = `${hours || '00'}:${mins || '00'}${displaySeconds ? `:${sec || '00'}` : ''}`;

	return result;
};

export const getDateTimeFormat = userSelf => `${get(userSelf.settings, 'date_format')} ${get(userSelf.settings, 'time_format')}`;

export const getFormattedTotalTime = (totalMinutes) => {
	let minutes = Math.round((totalMinutes % 60) || 0);
	let hours = Math.round(((totalMinutes - minutes) / 60) || 0);

	if (minutes === 60) {
		hours += 1;
		minutes = 0;
	}

	return `${hours || 0}h ${minutes || 0}min`;
};

export const getIsRequired = ({ userSelf, objectType, fieldKey }) => {
	const requiredFields = get(userSelf, `settings.required_${objectType}_fields`, []) || [];
	return requiredFields.includes(fieldKey);
};

export const serialize = (obj) => {
	const str = [];

	Object.keys(obj).forEach((key) => {
		const value = obj[key];

		if (Array.isArray(value)) {
			if (!value.length) return;

			value.forEach((v) => {
				if (v || typeof v === 'number') {
					str.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
				}
			});

			return;
		}

		if (!Number.isFinite(value) && !value) return;

		str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
	});

	return str.join('&');
};

export const getSignatureTypes = (userSelf, type, isCrs) => {
	let signatureTypes = get(userSelf, 'active_organization.signature_types') || [];

	if (type) {
		signatureTypes = signatureTypes.filter(s => s.type === type && (isCrs ? s.is_crs : true));
	}

	return signatureTypes;
};

export const getUserAllowedSignatureTypes = (userSelf, type, isCrs) => {
	let userAllowed = get(userSelf, 'active_organization.signature_types') || [];

	if (type) {
		userAllowed = userAllowed.filter(s => s.type === type && (isCrs ? s.is_crs : s.is_crs === false));
	}

	if (userSelf.is_superadmin) {
		return userAllowed;
	}

	return userAllowed.filter(st => (get(userSelf, 'permission_group.signature_type_ids') || []).includes(st.id));
};
